import { LoaderCircle } from 'lucide-react'
import {
  Navigate,
  Route,
  Routes,
} from 'react-router-dom'

import { useAuth } from './context/AuthContext'
import { useEmpresa } from './context/EmpresaContext'
import AppLayout from './layouts/AppLayout'
import Bodegas from './pages/Bodegas'
import MovimientosBodega from './pages/bodega/MovimientosBodega'
import NuevaEntradaBodega from './pages/bodega/NuevaEntradaBodega'
import NuevaSalidaBodega from './pages/bodega/NuevaSalidaBodega'
import SaldosBodega from './pages/bodega/SaldosBodega'
import SaldosIniciales from './pages/bodega/SaldosIniciales'
import TransferenciaBodega from './pages/bodega/TransferenciaBodega'
import CajasRejillas from './pages/CajasRejillas'
import CategoriasGasto from './pages/CategoriasGasto'
import CategoriasProducto from './pages/CategoriasProducto'
import Clientes from './pages/Clientes'
import Compras from './pages/compras/Compras'
import DetalleCompra from './pages/compras/DetalleCompra'
import NuevaCompra from './pages/compras/NuevaCompra'
import CuentasPorPagar from './pages/cuentasPorPagar/CuentasPorPagar'
import DetalleCuentaPorPagar from './pages/cuentasPorPagar/DetalleCuentaPorPagar'
import Dashboard from './pages/Dashboard'
import EditarGasto from './pages/EditarGasto'
import EditarIngreso from './pages/EditarIngreso'
import Empresas from './pages/Empresas'
import Gastos from './pages/Gastos'
import Ingresos from './pages/Ingresos'
import Login from './pages/Login'
import NuevoGasto from './pages/NuevoGasto'
import NuevoIngreso from './pages/NuevoIngreso'
import AnaliticaPos from './pages/pos/AnaliticaPos'
import EditarVentaPos from './pages/pos/EditarVentaPos'
import NuevaVentaPos from './pages/pos/NuevaVentaPos'
import PagosPendientesPos from './pages/pos/PagosPendientesPos'
import VentasPos from './pages/pos/VentasPos'
import Productos from './pages/Productos'
import Proveedores from './pages/Proveedores'
import UnidadesMedida from './pages/UnidadesMedida'
import Usuarios from './pages/Usuarios'

function App() {
  const {
    session,
    loading,
    authError,
    cerrarSesion,
  } = useAuth()

  const {
    empresaActual,
    loadingEmpresas,
    errorEmpresas,
  } = useEmpresa()

  if (loading || (session && loadingEmpresas)) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100">
        <div className="text-center">
          <LoaderCircle
            size={42}
            className="mx-auto animate-spin text-emerald-600"
          />

          <p className="mt-4 font-semibold text-slate-600">
            Cargando plataforma...
          </p>
        </div>
      </main>
    )
  }

  if (authError || errorEmpresas) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
        <div className="w-full max-w-md rounded-2xl border border-red-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold text-red-600">
            No fue posible cargar la plataforma
          </h1>

          <p className="mt-3 text-slate-600">
            {authError || errorEmpresas}
          </p>

          <button
            type="button"
            onClick={cerrarSesion}
            className="mt-6 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white"
          >
            Cerrar sesión
          </button>
        </div>
      </main>
    )
  }

  if (!session) {
    return <Login />
  }

  if (!empresaActual) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-100 p-6">
        <div className="rounded-2xl bg-white p-8 text-center shadow-sm">
          <h1 className="text-xl font-bold text-slate-900">
            Sin empresa asignada
          </h1>

          <p className="mt-3 text-slate-600">
            Tu usuario no tiene acceso a ninguna empresa.
          </p>

          <button
            type="button"
            onClick={cerrarSesion}
            className="mt-6 rounded-lg bg-slate-800 px-5 py-2 font-semibold text-white"
          >
            Cerrar sesión
          </button>
        </div>
      </main>
    )
  }

  const rolActual = empresaActual.rol
  const esPosVenta = rolActual === 'pos_venta'

  return (
    <Routes>
      <Route element={<AppLayout />}>
        {esPosVenta ? (
          <>
            <Route
              index
              element={<Navigate to="/pos/nueva-venta" replace />}
            />

            <Route
              path="pos/nueva-venta"
              element={<NuevaVentaPos />}
            />

            <Route
              path="pos/ventas"
              element={<VentasPos />}
            />

            <Route
              path="pos/ventas/:ventaId/editar"
              element={<EditarVentaPos />}
            />

            <Route
              path="pos/pagos-pendientes"
              element={<PagosPendientesPos />}
            />

            <Route
              path="pos/analitica"
              element={<AnaliticaPos />}
            />

            <Route
              path="*"
              element={<Navigate to="/pos/nueva-venta" replace />}
            />
          </>
        ) : (
          <>
            <Route
              index
              element={<Dashboard />}
            />

            <Route
              path="gastos"
              element={<Gastos />}
            />
            <Route
              path="gastos/nuevo"
              element={<NuevoGasto />}
            />

            <Route
              path="gastos/:gastoId/editar"
              element={<EditarGasto />}
            />

            <Route
              path="ingresos"
              element={<Ingresos />}
            />

            <Route
              path="ingresos/nuevo"
              element={<NuevoIngreso />}
            />

            <Route
              path="ingresos/:ingresoId/editar"
              element={<EditarIngreso />}
            />

            <Route
              path="contabilidad/cuentas-por-pagar"
              element={<CuentasPorPagar />}
            />

            <Route
              path="contabilidad/cuentas-por-pagar/:compraId"
              element={<DetalleCuentaPorPagar />}
            />

            <Route
              path="proveedores"
              element={<Proveedores />}
            />

            <Route
              path="compras"
              element={<Compras />}
            />

            <Route
              path="compras/nueva"
              element={<NuevaCompra />}
            />

            <Route
              path="compras/:compraId"
              element={<DetalleCompra />}
            />

            <Route
              path="pos/nueva-venta"
              element={<NuevaVentaPos />}
            />

            <Route
              path="pos/ventas"
              element={<VentasPos />}
            />

            <Route
              path="pos/ventas/:ventaId/editar"
              element={<EditarVentaPos />}
            />

            <Route
              path="pos/pagos-pendientes"
              element={<PagosPendientesPos />}
            />

            <Route
              path="pos/analitica"
              element={<AnaliticaPos />}
            />

            <Route
              path="bodega/saldos"
              element={<SaldosBodega />}
            />

            <Route
              path="bodega/movimientos"
              element={<MovimientosBodega />}
            />

            <Route
              path="bodega/saldos-iniciales"
              element={<SaldosIniciales />}
            />

            <Route
              path="bodega/entrada"
              element={<NuevaEntradaBodega />}
            />

            <Route
              path="bodega/salida"
              element={<NuevaSalidaBodega />}
            />

            <Route
              path="bodega/transferencia"
              element={<TransferenciaBodega />}
            />

            <Route
              path="mantenedores/categorias"
              element={<CategoriasGasto />}
            />

            <Route
              path="mantenedores/clientes"
              element={<Clientes />}
            />

            <Route
              path="mantenedores/empresas"
              element={<Empresas />}
            />

            <Route
              path="mantenedores/usuarios"
              element={<Usuarios />}
            />

            <Route
              path="mantenedores/bodegas"
              element={<Bodegas />}
            />

            <Route
              path="mantenedores/productos"
              element={<Productos />}
            />

            <Route
              path="mantenedores/categorias-producto"
              element={<CategoriasProducto />}
            />

            <Route
              path="mantenedores/unidades-medida"
              element={<UnidadesMedida />}
            />

            <Route
              path="mantenedores/cajas-rejillas"
              element={<CajasRejillas />}
            />

            <Route
              path="*"
              element={<Navigate to="/" replace />}
            />
          </>
        )}
      </Route>
    </Routes>
  )
}

export default App